import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import meKeys from '@/api/queries/me/keyFactory';
import {
  CreateCustomFieldGroupMutation as _CreateCustomFieldGroupMutation,
  CreateCustomFieldGroupInput,
  MyCustomFieldGroupsQuery,
} from '@/gql/graphql';
import useCreateCustomFieldGroup from '../useCreateCustomFieldGroup';

const useOptimisticCreateCustomFieldGroup = (
  options?: UseMutationOptions<
    _CreateCustomFieldGroupMutation,
    unknown,
    CreateCustomFieldGroupInput
  >
) => {
  const queryClient = useQueryClient();

  return useCreateCustomFieldGroup({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: meKeys.customFieldGroups(),
      });

      // Snapshot the previous value
      const previousCustomFieldGroups = queryClient.getQueryData(
        meKeys.customFieldGroups()
      );

      const newCustomFieldGroup = {
        ...variables,
        id: variables.id || 'temp-id',
        customFields: [],
      };

      // Optimistically update to the new value
      queryClient.setQueryData<MyCustomFieldGroupsQuery>(
        meKeys.customFieldGroups(),
        (old) => {
          console.log({ old, newCustomFieldGroup });
          if (!old) return;
          return {
            me: {
              user: {
                account: {
                  customFieldGroups: [
                    ...old.me.user.account.customFieldGroups,
                    newCustomFieldGroup,
                  ],
                },
              },
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomFieldGroups: previousCustomFieldGroups };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        meKeys.customFieldGroups(),
        // @ts-expect-error
        context.previousProjectOverview
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
      queryClient.invalidateQueries({
        queryKey: meKeys.customFieldGroups(),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticCreateCustomFieldGroup;
